// Quick start for the head-term hubs: pick a voice here and land in the
// studio at /create with it preselected. Popular voices come from the hub's
// own SSR list, so the card renders server-side with no extra fetch; search
// uses the studio picker's /api/model params.
import React, { useEffect, useState } from "react";
import axios from "axios";
import Link from "next/link";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import { ArrowRight, Search, X } from "lucide-react";
import { VoiceImage } from "@/components/studio/StudioChrome";
import { useT, type Locale } from "@jammable/ui-core/src/i18n";

/* eslint-disable @typescript-eslint/no-explicit-any */

const SHOWN = 8;

const searchVoices = async (search: string): Promise<any[]> => {
  const { data } = await axios.get("/api/model", {
    params: {
      search,
      limit: SHOWN,
      page: 1,
      sortOrder: "desc",
      sortBy: "usesLast24Hours",
    },
  });
  return data?.data ?? [];
};

const EmbeddedCreateWidget = ({
  popular = [],
  locale = "en",
}: {
  popular?: any[];
  // The hub page's language (server-rendered, so not the visitor's).
  locale?: Locale;
}) => {
  const router = useRouter();
  const t = useT(locale);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const results = useQuery(
    ["hub-quickstart-search", debounced],
    () => searchVoices(debounced),
    { enabled: !!debounced, keepPreviousData: true, staleTime: 60 * 1000 },
  );

  const voices: any[] = debounced
    ? (results.data ?? [])
    : popular.slice(0, SHOWN);
  const loading = !!debounced && results.isFetching && voices.length === 0;

  const pick = (voice: any) =>
    router.push(
      "/create?voice=" + encodeURIComponent(voice.urlParam || voice._id),
    );

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-4 tw-rounded-[30px] tw-bg-white tw-p-4 tw-text-jam-ink md:tw-rounded-[36px] md:tw-p-6 lg:tw-w-[460px] lg:tw-flex-shrink-0">
      <span className="jam-display tw-px-1 tw-text-[22px] tw-font-extrabold tw-leading-none md:tw-text-[26px]">
        {t("widget.title")}
      </span>

      <label className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-full tw-bg-jam-lav tw-px-5 tw-py-3.5">
        <Search
          size={18}
          strokeWidth={2.5}
          className="tw-shrink-0 tw-text-jam-muted"
        />
        <input
          aria-label={t("widget.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("widget.search")}
          className="tw-w-full tw-min-w-0 tw-border-0 tw-bg-transparent tw-text-[15px] tw-font-bold tw-text-jam-ink tw-outline-none placeholder:tw-text-jam-muted"
          autoComplete="off"
          spellCheck={false}
        />
        {search && (
          <button
            type="button"
            aria-label={t("widget.clear")}
            onClick={() => setSearch("")}
            className="tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-jam-muted"
          >
            <X size={18} />
          </button>
        )}
      </label>

      <div className="tw-px-1 tw-text-[13px] tw-font-extrabold tw-text-jam-muted">
        {debounced
          ? t("widget.matching", { q: debounced })
          : t("widget.popular")}
      </div>

      {loading ? (
        <div className="tw-grid tw-grid-cols-4 tw-gap-2">
          {Array.from({ length: SHOWN }).map((_, i) => (
            <div
              key={i}
              className="tw-h-[104px] tw-animate-pulse tw-rounded-[22px] tw-bg-jam-lav"
            />
          ))}
        </div>
      ) : voices.length === 0 ? (
        <div className="tw-rounded-[22px] tw-bg-jam-lav tw-p-6 tw-text-center tw-text-sm tw-font-bold tw-text-jam-muted">
          {debounced ? t("widget.noMatch") : t("widget.searchAny")}
        </div>
      ) : (
        <div
          className={`tw-grid tw-grid-cols-4 tw-gap-2 ${results.isFetching ? "tw-opacity-60" : ""}`}
        >
          {voices.map((v) => (
            <button
              key={v._id}
              type="button"
              onClick={() => pick(v)}
              aria-label={t("widget.coverWith", { name: v.artistname })}
              className="tw-flex tw-min-w-0 tw-cursor-pointer tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-[22px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-px-1.5 tw-pb-2.5 tw-pt-3 tw-text-jam-ink tw-transition-colors hover:tw-border-jam-purple"
            >
              <span className="tw-relative tw-h-[52px] tw-w-[52px] tw-overflow-hidden tw-rounded-full tw-bg-jam-line">
                <VoiceImage
                  model={v}
                  fill
                  sizes="52px"
                  className="tw-object-cover"
                />
              </span>
              <span className="tw-line-clamp-1 tw-w-full tw-text-center tw-text-xs tw-font-extrabold">
                {v.artistname}
              </span>
            </button>
          ))}
        </div>
      )}

      <Link
        href="/create"
        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-jam-lime tw-px-6 tw-py-4 tw-text-[15px] tw-font-extrabold tw-text-jam-ink tw-no-underline"
      >
        {t("widget.start")}
        <ArrowRight size={18} strokeWidth={2.75} />
      </Link>

      <p className="tw-m-0 tw-px-1 tw-text-center tw-text-xs tw-font-semibold tw-leading-snug tw-text-jam-muted">
        {t("widget.rights")}
      </p>
    </div>
  );
};

export default EmbeddedCreateWidget;
